import { useMemo, useState } from "react";
import type { Station } from "../lib/types";
import { classify, scaleFor, type Param, type Rating } from "../lib/params";
import { formatValue } from "@core/lib/format";

interface Props {
  stations: Station[];
  param: Param;
  terciles: [number, number] | null;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  scopeName: string | null;
}

export default function StationList({
  stations, param, terciles, selectedId, onSelect, scopeName,
}: Props) {
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState<Rating | "all">("all");
  const scale = scaleFor(param);

  const rated = useMemo(
    () => stations.map((s) => ({
      s,
      latest: s.latest[param.key],
      rating: classify(param, s.latest[param.key]?.v, terciles),
    })),
    [stations, param, terciles],
  );

  const counts = useMemo(() => {
    const c = { all: stations.length } as Record<string, number>;
    for (const r of ["good", "fair", "poor", "none"] as Rating[]) {
      c[r] = rated.filter((x) => x.rating === r).length;
    }
    return c;
  }, [rated, stations.length]);

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return rated
      .filter((x) => filter === "all" || x.rating === filter)
      .filter(({ s }) =>
        !needle ||
        s.siteId.toLowerCase().includes(needle) ||
        s.name.toLowerCase().includes(needle) ||
        s.waterbody.toLowerCase().includes(needle))
      // Stations without the selected parameter sink, rather than sitting at
      // the top with an em dash where a number should be.
      .sort((a, b) => {
        if (!a.latest !== !b.latest) return a.latest ? -1 : 1;
        return a.s.waterbody.localeCompare(b.s.waterbody) || a.s.name.localeCompare(b.s.name);
      });
  }, [rated, q, filter]);

  const chips = (["good", "fair", "poor", "none"] as Rating[]).filter((r) => counts[r] > 0);

  return (
    <div className="list">
      <div className="list-controls">
        <input
          className="search"
          type="search"
          placeholder="Search station, name or waterbody"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          aria-label="Search monitoring stations"
        />
        {chips.length > 1 && (
          <div className="chips" role="group" aria-label={`Filter by ${param.label}`}>
            <button
              className={"chip" + (filter === "all" ? " on" : "")}
              onClick={() => setFilter("all")}
              aria-pressed={filter === "all"}
            >
              All <b>{counts.all}</b>
            </button>
            {chips.map((r) => (
              <button
                key={r}
                className={"chip" + (filter === r ? " on" : "")}
                onClick={() => setFilter(filter === r ? "all" : r)}
                aria-pressed={filter === r}
                style={{ ["--chip" as string]: scale[r].color }}
              >
                <i className="dot" /> {scale[r].label} <b>{counts[r]}</b>
              </button>
            ))}
          </div>
        )}
      </div>

      <ul className="rows">
        {shown.map(({ s, latest, rating }) => {
          const on = s.key === selectedId;
          return (
            <li key={s.key}>
              <button
                className={"row" + (on ? " on" : "")}
                onClick={() => onSelect(on ? null : s.key)}
                aria-current={on ? "true" : undefined}
              >
                <span className="row-bar" style={{ background: scale[rating].color }} />
                <span className="row-main">
                  <span className="row-id">{s.siteId}</span>
                  <span className="row-name">{s.name}</span>
                  <span className="row-meta">
                    {s.waterbody}
                    {latest ? ` · ${latest.y} · ${latest.d.toLowerCase()}` : " · not measured here"}
                  </span>
                </span>
                <span className="row-reading">
                  <span className="row-reading-label">{param.short}</span>
                  <span className="row-reading-value" style={{ color: scale[rating].color }}>
                    {formatValue(latest?.v ?? null, param.digits)}
                  </span>
                  <span className="row-reading-unit">{param.unit || " "}</span>
                </span>
              </button>
            </li>
          );
        })}
        {shown.length === 0 && (
          <li className="empty-row">
            No stations match that{scopeName ? ` on ${scopeName}` : ""}.
          </li>
        )}
      </ul>
    </div>
  );
}
